import { useUpdateUser } from "@hooks/trpc/useMe";
import { captureException } from "@utils/captureException";
import { useAuth } from "@utils/firebaseAuth/auth";
import { applyReferral, readReferrerId } from "@utils/links/referral";
import { useEffect, useRef, useState } from "react";
import { Linking } from "react-native";

const TAG = "::ReferralLinkHandler::";

/**
 * Ingests refer-a-friend links (`?action=refer-friend&ref=<userId>`).
 *
 * Shares are plain universal links now, so a referral arrives on the URL
 * itself rather than through the Branch SDK callback — this reads it straight
 * off the incoming link (cold start and while running). Branch custom-action
 * links still work for the old links in the wild; useBranch handles those and
 * ends up in the same `applyReferral`.
 *
 * The referrer is held until the user is signed in, because recording it is a
 * write against their account: a referral that lands mid-signup is applied as
 * soon as auth settles.
 */
const ReferralLinkHandler = () => {
  const { firebaseUser: user, status } = useAuth();
  const updateUser = useUpdateUser();
  const [referrerId, setReferrerId] = useState<string | null>(null);
  const appliedReferrerRef = useRef<string | null>(null);

  useEffect(() => {
    const ingest = (url: string | null | undefined) => {
      const incomingReferrerId = url ? readReferrerId(url) : null;
      if (!incomingReferrerId) return;

      console.log(TAG, "referral link received");
      setReferrerId(incomingReferrerId);
    };

    Linking.getInitialURL()
      .then(ingest)
      .catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "referral",
          operation: "get-initial-url",
        });
      });

    const subscription = Linking.addEventListener("url", ({ url }) =>
      ingest(url),
    );

    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (!referrerId || status !== "logged-in" || !user) return;
    // `updateUser` is a fresh object on every mutation state change, so the
    // effect re-runs; the ref keeps a referral one-shot.
    if (appliedReferrerRef.current === referrerId) return;
    appliedReferrerRef.current = referrerId;

    console.log(TAG, "applying referral");
    applyReferral({ referrerId, userId: user.uid, updateUser }).catch(
      (error) => {
        appliedReferrerRef.current = null;
        captureException(error, {
          channel: "error",
          area: "referral",
          operation: "apply-referral",
        });
      },
    );
  }, [referrerId, status, user, updateUser]);

  return null;
};

export default ReferralLinkHandler;
